<template>
  <div class="mx-auto max-w-2xl px-6 py-8">
    <div v-if="!submitted">

      <Commerce-Breadcrumb :items="[
        { label: 'خانه', to: '/' },
        { label: 'سفارش اختصاصی' }
      ]" />

      <h1 class="mt-4 text-2xl font-extrabold md:text-3xl">
        ثبت سفارش اختصاصی
      </h1>

      <p class="mt-2 text-muted-foreground">
        برای پرداخت‌هایی که در فهرست خدمات نیستند. مشخصات را وارد کنید تا برای بررسی ارسال شود.
      </p>

      <Commerce-Notice tone="info">
        <span class="font-medium">
          در سفارش اختصاصی، مبلغ بلافاصله قطعی و پرداخت نمی‌شود.
        </span>
        پس از بررسی، پیشنهاد پرداخت به شما اعلام می‌گردد.
      </Commerce-Notice>

      <Commerce-Card class="mt-6 flex flex-col gap-4 p-6">

        <UiForm ref="formRef" :validation-schema="validationSchema" :initial-values="initialValues" @submit="submit"
          class="flex flex-col gap-4">

          <Commerce-Field label="نام سرویس یا وب‌سایت">
            <Commerce-TextField name="service_name" placeholder="مثلاً پرداخت هزینهٔ اپلیکیشن دانشگاه" />
          </Commerce-Field>

          <Commerce-Field label="لینک اصلی سرویس">
            <Commerce-TextField name="service_url" placeholder="https://" dir="ltr" />
          </Commerce-Field>

          <Commerce-Field label="لینک صفحهٔ پرداخت">
            <Commerce-TextField name="payment_url" placeholder="https://" dir="ltr" />
          </Commerce-Field>

          <div class="grid gap-4 sm:grid-cols-[1fr_140px]">

            <Commerce-Field label="مبلغ">
              <Commerce-TextField name="amount" numeric :max="MAX_AMOUNT" placeholder="مثلاً ۷۵" inputmode="decimal" />
            </Commerce-Field>

            <Commerce-Field label="ارز">
              <Commerce-Select name="currency_id" :options="currencyOptions" />
            </Commerce-Field>

          </div>

          <Commerce-Field label="کشور یا ریجن">
            <Commerce-Select name="country_id" :options="countryOptions" />
          </Commerce-Field>

          <Commerce-Field label="شرح درخواست">

            <Commerce-Textarea name="description" rows="3"
              placeholder="توضیح دهید چه پرداختی نیاز دارید و چه اطلاعاتی لازم است." />

          </Commerce-Field>

          <Commerce-Field label="فایل یا تصویر" optional hint="در صورت نیاز، تصویر صفحهٔ پرداخت را پیوست کنید">
            <Commerce-FileUpload v-model="attachment" />
          </Commerce-Field>

          <Commerce-Checkbox v-model:checked="agree" shape="circle">
            تأیید می‌کنم سفارش اختصاصی فقط برای خدمات و پرداخت‌های دیجیتال قانونی است و پس از ثبت قابل لغو نیست.
          </Commerce-Checkbox>

          <Commerce-Button :loading="loadingSubmit" :disabled="!agree" size="lg" full type="submit">
            {{ isLoggedIn ? 'ارسال برای بررسی' : 'ورود و ارسال برای بررسی' }}
          </Commerce-Button>

        </UiForm>

      </Commerce-Card>
    </div>

    <div v-else class="py-16">

      <Commerce-EmptyState glyph="✓" title="درخواست شما ثبت شد"
        body="وضعیت اولیه: در انتظار بررسی. پس از بررسی، مبلغ ارزی، نرخ، کارمزد و مبلغ نهایی ریالی به شما اعلام می‌شود. مهلت پرداخت پس از اعلام پیشنهاد ۲۴ ساعت است.">

        <template #action>

          <Commerce-Button @click="navigateTo(auth.value ? '/orders' : '/')">
            {{ auth.value ? 'مشاهدهٔ سفارش‌ها' : 'بازگشت به خانه' }}
          </Commerce-Button>

        </template>

      </Commerce-EmptyState>

    </div>

  </div>
</template>
<script setup lang="ts">
const siteUrl = useSiteUrl()

import { computed, nextTick, onMounted, ref } from 'vue'
import { boolean, string } from 'yup'
import { useSeoMeta } from '#app'
import { getErrorMessage } from '@/utils/errorUtils'
import { getCountryFaName } from '@/utils/get-country-fa-name'
import { createCustomOrderAction } from '~/api/functions/orders/create-custom-order.action'
// import type { CreateCustomOrderPayload } from '~/services/api/typings'
import { useApi } from '~/composables/useApi'
import { useSettings } from '~/composables/useSettings'

useSeoMeta({
  title: 'ثبت سفارش اختصاصی - پی‌جیب',
  description:
    'برای پرداخت‌هایی که در فهرست خدمات نیستند، مشخصات درخواست خود را ثبت کنید تا پس از بررسی پیشنهاد پرداخت به شما اعلام شود.',

  robots: 'noindex,follow',

  ogTitle: 'ثبت سفارش اختصاصی - پی‌جیب',
  ogDescription:
    'برای پرداخت‌هایی که در فهرست خدمات نیستند، مشخصات درخواست خود را ثبت کنید تا پس از بررسی پیشنهاد پرداخت به شما اعلام شود.',
  ogImage: siteUrl('/services/payjib.webp'),
  ogUrl: siteUrl('/services/custom'),
  ogType: 'website',
  ogSiteName: 'پی‌جیب',
  ogLocale: 'fa_IR',

  twitterCard: 'summary_large_image',
  twitterTitle: 'ثبت سفارش اختصاصی - پی‌جیب',
  twitterDescription:
    'برای پرداخت‌هایی که در فهرست خدمات نیستند، مشخصات درخواست خود را ثبت کنید تا پس از بررسی پیشنهاد پرداخت به شما اعلام شود.',
  twitterImage: siteUrl('/services/payjib.webp'),
})

const route = useRoute()
const router = useRouter()
const { auth, initAuth } = useAuth()
const { toast } = useToast()

const formReady = ref(false)
const attachment = ref<File | null>(null)
const submitted = ref(false)
const loadingSubmit = ref(false)
const agree = ref(false)
const MAX_AMOUNT = 10000
const initialValues = ref({
  service_name: '',
  service_url: '',
  payment_url: '',
  amount: '',
  currency_id: '',
  country_id: '',
  description: '',
  // agree: false,
})

const validationSchema = {
  service_name: string().required('نام سرویس الزامی است'),

  service_url: string().required('لینک سرویس الزامی است').url('لینک وارد شده معتبر نیست'),

  payment_url: string().required('لینک پرداخت الزامی است').url('لینک پرداخت معتبر نیست'),

  amount: string()
    .required('مبلغ الزامی است')
    .matches(/^\d+(\.\d{1,2})?$/, 'مبلغ وارد شده معتبر نیست')
    .test('min', 'مبلغ باید بیشتر از صفر باشد', (v) => Number(v) > 0)
    .test('max', `حداکثر مبلغ ${MAX_AMOUNT.toLocaleString('fa-IR')} است`, (v) => Number(v) <= MAX_AMOUNT),

  currency_id: string().required('انتخاب ارز الزامی است'),

  country_id: string().required('انتخاب کشور یا ریجن الزامی است'),

  description: string().required('شرح درخواست الزامی است'),

  // agree: boolean()
  //   .oneOf([true], 'تأیید قوانین الزامی است'),
}

const { countries, currencies } = useSettings()

const isLoggedIn = computed(() => !!auth.value)

const currencyOptions = computed(() =>
  currencies.value.map((currency: any) => ({
    label: currency.label,
    value: currency.value,
  }))
)

const countryOptions = computed(() =>
  countries.value.map((country: any) => ({
    label: getCountryFaName(country.iso),
    value: country.value,
  }))
)

function navigateTo(path: string) {
  router.push(path)
}

async function submit(values: Record<string, any>) {
  if (!auth.value) {
    sessionStorage.setItem(
      'customOrderForm',
      JSON.stringify({
        values,
        agree: agree.value,
      })
    )

    console.log('SAVED FORM:', sessionStorage.getItem('customOrderForm'))

    router.push({
      path: '/auth',
      query: {
        mode: 'login',
        returnTo: route.fullPath,
      },
    })

    return
  }

  try {
    loadingSubmit.value = true

    const payload = {
      service_name: values.service_name,
      service_url: values.service_url,
      payment_url: values.payment_url || undefined,
      amount: values.amount,
      currency_id: values.currency_id,
      country_id: values.country_id || undefined,
      description: values.description,
      attachment: attachment.value || undefined,
    }

    await createCustomOrderAction(payload)

    submitted.value = true

    toast.success('درخواست سفارش اختصاصی ثبت شد')
  } catch (error) {
    toast.error(getErrorMessage(error))
  } finally {
    loadingSubmit.value = false
  }
}

const formRef = ref()

onMounted(async () => {
  await initAuth()

  const savedForm = sessionStorage.getItem('customOrderForm')

  formReady.value = true

  if (savedForm) {
    const { values, agree: savedAgree } = JSON.parse(savedForm)

    await nextTick()

    formRef.value?.setValues(values)

    agree.value = !!savedAgree

    sessionStorage.removeItem('customOrderForm')
  }
})
</script>
